/**
 * `flasher_args.json` as something a flash can be run from.
 *
 * The build writes that file into the build directory next to the images, and
 * it is the only place where the offsets, the flash settings and the chip are
 * written down together. All three come out of the project's sdkconfig and its
 * partition table, so nothing here may assume the values the ESP-IDF example
 * happens to produce — a project with a different partition table puts the app
 * somewhere other than 0x10000, and writing it to the wrong offset is a bricked
 * boot rather than a failed flash.
 *
 * Two steps, because the bytes arrive from somewhere else. Parsing is pure and
 * so can be tested against a real file from this toolchain; loading takes an
 * injected reader, since the images are stored in the runtime worker's
 * filesystem and transferred via messages.
 */

/** One image and where it goes, with the path made absolute in the guest. */
export interface FlashFileSpec {
    address: number;
    path: string;
}

/** What the file says, before any of it has been read. */
export interface FlashPlanSpec {
    chip: string;
    flashMode: FlashMode;
    flashSize: FlashSize;
    files: FlashFileSpec[];
}

export interface FlashFile extends FlashFileSpec {
    data: Uint8Array;
}

/** The same thing with the images in hand, which is what esptool-js takes. */
export interface FlashPlan {
    chip: string;
    flashMode: FlashMode;
    flashSize: FlashSize;
    files: FlashFile[];
}

/**
 * The values esptool-js will accept, as its `FlashModeValues` and
 * `FlashSizeValues` list them. They are unions rather than strings so that a
 * setting it has never heard of is refused here, with the name of the field
 * it came from, instead of deep inside a flash.
 */
const FLASH_MODES = ["keep", "dio", "qio", "dout", "qout"] as const;
const FLASH_SIZES = [
    "detect",
    "keep",
    "256KB",
    "512KB",
    "1MB",
    "2MB",
    "2MB-c1",
    "4MB",
    "4MB-c1",
    "8MB",
    "16MB",
    "32MB",
    "64MB",
    "128MB",
] as const;

export type FlashMode = (typeof FLASH_MODES)[number];
export type FlashSize = (typeof FLASH_SIZES)[number];

export function parseFlashPlan(json: string, buildDir: string): FlashPlanSpec {
    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch (e) {
        throw new Error(`flasher_args.json is not JSON: ${e instanceof Error ? e.message : String(e)}`);
    }

    const root = asObject(parsed, "the file");
    const flashFiles = asObject(root["flash_files"], "flash_files");
    const entries = Object.entries(flashFiles);
    if (entries.length === 0) {
        throw new Error("flasher_args.json: flash_files is empty, so there is nothing to write");
    }

    // Sorted by address because that is the order esptool writes them and the
    // order they are worth showing in: bootloader, partition table, app.
    const files = entries
        .map(([offset, value]) => {
            if (typeof value !== "string") {
                throw new Error(`flasher_args.json: the image at ${offset} is ${show(value)}, expected a path`);
            }
            return { address: parseOffset(offset), path: under(buildDir, value) };
        })
        .sort((a, b) => a.address - b.address);

    const settings = asObject(root["flash_settings"], "flash_settings");
    const extra = asObject(root["extra_esptool_args"], "extra_esptool_args");
    const chip = extra["chip"];
    if (typeof chip !== "string") {
        throw new Error(`flasher_args.json: extra_esptool_args.chip is ${show(chip)}, expected a chip name`);
    }

    return {
        chip,
        flashMode: oneOf(FLASH_MODES, settings["flash_mode"], "flash_settings.flash_mode"),
        flashSize: oneOf(FLASH_SIZES, settings["flash_size"], "flash_settings.flash_size"),
        files,
    };
}

/**
 * The images, in one call, because the reader crosses a thread boundary and
 * three round trips would cost three of them.
 */
export async function loadFlashPlan(
    spec: FlashPlanSpec,
    readFiles: (paths: string[]) => Promise<Uint8Array[]>,
): Promise<FlashPlan> {
    const bytes = await readFiles(spec.files.map((file) => file.path));
    if (bytes.length !== spec.files.length) {
        throw new Error(
            `Asked for ${spec.files.length} images out of the build directory and got ${bytes.length} back.`,
        );
    }
    const files = spec.files.map((file, index) => {
        const data = bytes[index];
        if (data === undefined) throw new Error(`No bytes came back for ${file.path}.`);
        return { ...file, data };
    });
    return { ...spec, files };
}

/**
 * The plan's images in one flash image from address 0, with 0xff between
 * them as on erased flash, for an emulator that takes a single file.
 */
export function mergeImages(plan: FlashPlan): Uint8Array {
    const size = Math.max(...plan.files.map((file) => file.address + file.data.length));
    const image = new Uint8Array(size).fill(0xff);
    for (const file of plan.files) image.set(file.data, file.address);
    return image;
}

/** The flash size the build is for, in bytes; 0 when it is left to detection. */
export function flashSizeBytes(size: FlashSize): number {
    const match = /^(\d+)(KB|MB)/.exec(size);
    if (match === null) return 0;
    return Number(match[1]) * (match[2] === "KB" ? 1024 : 1024 * 1024);
}

/** The partition table's offset in a build's `flasher_args.json`, if it names one. */
export function tableOffsetOf(flasherArgs: string): number | undefined {
    try {
        const offset: unknown = JSON.parse(flasherArgs)?.["partition-table"]?.offset;
        return typeof offset === "string" && /^0x[0-9a-f]+$/i.test(offset) ? Number.parseInt(offset, 16) : undefined;
    } catch {
        return undefined;
    }
}

function parseOffset(offset: string): number {
    if (!/^0x[0-9a-fA-F]+$/.test(offset)) {
        throw new Error(
            `flasher_args.json: "${offset}" is not a flash offset; the keys of flash_files are hex, like "0x10000"`,
        );
    }
    return Number.parseInt(offset, 16);
}

/** A path from the file, which is relative to the build directory, made whole. */
function under(buildDir: string, path: string): string {
    return `${buildDir.replace(/\/+$/, "")}/${path.replace(/^(\.\/|\/)+/, "")}`;
}

function asObject(value: unknown, what: string): Record<string, unknown> {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
        throw new Error(`flasher_args.json: ${what} is ${show(value)}, expected an object`);
    }
    return value as Record<string, unknown>;
}

function oneOf<T extends string>(allowed: readonly T[], value: unknown, what: string): T {
    const found = typeof value === "string" ? allowed.find((one) => one === value) : undefined;
    if (found === undefined) {
        throw new Error(`flasher_args.json: ${what} is ${show(value)}, expected one of ${allowed.join(", ")}`);
    }
    return found;
}

function show(value: unknown): string {
    return value === undefined ? "missing" : JSON.stringify(value);
}
